import { Flows } from '../constants/Flow';
import { useTranslation } from 'react-i18next';
import { useLineOfBusiness } from '../hooks/useLineOfBusiness';
import { ChatBoxMessage } from '../design_system/ChatBoxMessage';
import { v4 as uuidV4 } from 'uuid';
import { EventSource, EventType, MessageType } from '../graphql/graphql';
import { getLineOfBusinessName } from '../i18n/translations';
import React, { useMemo } from 'react';
import { useChatBoxStateStore } from '../stores/chatBoxStateStore';
import { useEventStore } from '../stores/eventStore';
import { LineOfBusinesses } from '../constants/LineOfBusiness';
const useDefaultWelcomeEvent = (persistedTimestamp) => {
    const { t } = useTranslation();
    const { lineOfBusiness } = useLineOfBusiness();
    return useMemo(() => ({
        id: uuidV4().toString(),
        persistedTimestamp: persistedTimestamp,
        source: EventSource.Advisor,
        type: EventType.Message,
        messageEvent: {
            messageType: MessageType.Text,
            text: {
                message: t('WELCOME_MESSAGE_TEXT', {
                    line_of_business_name: getLineOfBusinessName(lineOfBusiness),
                }),
            },
            from: 'Spotify',
        },
        readByCustomer: true,
        loading: false,
        failed: false,
        local: false,
        historic: false,
    }), [persistedTimestamp, lineOfBusiness, t]);
};
const useS4AEnWelcomeEvent = (persistedTimestamp) => {
    const { t } = useTranslation();
    return useMemo(() => ({
        id: uuidV4().toString(),
        persistedTimestamp: persistedTimestamp,
        source: EventSource.Advisor,
        type: EventType.Message,
        messageEvent: {
            messageType: MessageType.Text,
            text: {
                message: t('S4A_WELCOME_MESSAGE_TEXT'),
            },
            from: 'Spotify',
        },
        readByCustomer: true,
        loading: false,
        failed: false,
        local: false,
        historic: false,
    }), [persistedTimestamp, t]);
};
const useConsumerWelcomeEvent = (persistedTimestamp) => {
    const { t } = useTranslation();
    return useMemo(() => ({
        id: uuidV4().toString(),
        persistedTimestamp: persistedTimestamp,
        source: EventSource.Advisor,
        type: EventType.Message,
        messageEvent: {
            messageType: MessageType.Text,
            text: {
                message: t('CONSUMER_CONTACT_US_WELCOME_MESSAGE_TEXT'),
            },
            from: 'Spotify',
        },
        readByCustomer: true,
        loading: false,
        failed: false,
        local: false,
        historic: false,
    }), [persistedTimestamp, t]);
};
const useCloseFlowWelcomeEvent = (persistedTimestamp) => {
    const { t } = useTranslation();
    const { lineOfBusiness } = useLineOfBusiness();
    return useMemo(() => ({
        id: uuidV4().toString(),
        persistedTimestamp: persistedTimestamp,
        source: EventSource.Advisor,
        type: EventType.Message,
        messageEvent: {
            messageType: MessageType.Question,
            question: {
                text: t('CONSUMER_CLOSE_ACCOUNT_WELCOME_MESSAGE_TEXT', {
                    line_of_business_name: getLineOfBusinessName(lineOfBusiness),
                }),
                options: [
                    {
                        value: t('CLOSE_MY_ACCOUNT_BTN', {
                            line_of_business_name: getLineOfBusinessName(lineOfBusiness),
                        }),
                        label: t('CLOSE_MY_ACCOUNT_BTN', {
                            line_of_business_name: getLineOfBusinessName(lineOfBusiness),
                        }),
                    },
                    {
                        value: t('CANCEL_PREMIUM_BTN', {
                            line_of_business_name: getLineOfBusinessName(lineOfBusiness),
                        }),
                        label: t('CANCEL_PREMIUM_BTN', {
                            line_of_business_name: getLineOfBusinessName(lineOfBusiness),
                        }),
                    },
                    {
                        value: t('OTHER_HELP_BTN', {
                            line_of_business_name: getLineOfBusinessName(lineOfBusiness),
                        }),
                        label: t('OTHER_HELP_BTN', {
                            line_of_business_name: getLineOfBusinessName(lineOfBusiness),
                        }),
                    },
                ],
            },
        },
        readByCustomer: true,
        loading: false,
        failed: false,
        local: false,
        historic: false,
    }), [lineOfBusiness, persistedTimestamp, t]);
};
export const WelcomeMessageController = ({ event, onOptionClick, }) => {
    const { lineOfBusiness } = useLineOfBusiness();
    const { flow = Flows.CONTACT_US } = useChatBoxStateStore(lineOfBusiness);
    const { i18n } = useTranslation();
    const { events } = useEventStore();
    const isLastEvent = events.length <= 1 && !Boolean(event.historic);
    const DefaultWelcomeEvent = useDefaultWelcomeEvent(event.persistedTimestamp);
    const ConsumerWelcomeEvent = useConsumerWelcomeEvent(event.persistedTimestamp);
    const CloseFLowWelcomeEvent = useCloseFlowWelcomeEvent(event.persistedTimestamp);
    const S4AEnWelcomeEvent = useS4AEnWelcomeEvent(event.persistedTimestamp);
    if (lineOfBusiness === LineOfBusinesses.CONSUMER ||
        lineOfBusiness === LineOfBusinesses.CONSUMER_STAGING) {
        if (flow === Flows.CLOSE_ACCOUNT) {
            return (React.createElement(ChatBoxMessage, { onOptionClick: onOptionClick, isNotLastOption: !isLastEvent, event: CloseFLowWelcomeEvent }));
        }
        return React.createElement(ChatBoxMessage, { event: ConsumerWelcomeEvent });
    }
    if ((lineOfBusiness === LineOfBusinesses.S4A ||
        lineOfBusiness === LineOfBusinesses.S4A_STAGING) &&
        i18n.language === 'en-US') {
        return React.createElement(ChatBoxMessage, { event: S4AEnWelcomeEvent });
    }
    return React.createElement(ChatBoxMessage, { event: DefaultWelcomeEvent });
};
